import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";

import { orders } from "@/api";

const STORAGE_KEY = "ebmr.unit";

/**
 * The plant hierarchy and the selected production unit. Every page is
 * scoped to one unit, so this lives in the store rather than in each page,
 * and the choice survives reloads.
 */
export const loadOrganization = createAsyncThunk("unit/load", async () =>
  orders.getOrganization(),
);

const slice = createSlice({
  name: "unit",
  initialState: {
    organization: null,
    units: [],
    current: localStorage.getItem(STORAGE_KEY) || null,
    status: "idle",
  },
  reducers: {
    setUnit(state, action) {
      state.current = action.payload;
      localStorage.setItem(STORAGE_KEY, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadOrganization.pending, (state) => {
        state.status = "loading";
      })
      .addCase(loadOrganization.fulfilled, (state, action) => {
        state.status = "ready";
        state.organization = action.payload;
        state.units = (action.payload.sites || []).flatMap((site) =>
          (site.plants || []).flatMap((plant) =>
            (plant.units || []).map((unit) => ({
              ...unit,
              site: site.name,
              siteId: site.id,
              plant: plant.name,
              plantId: plant.id,
            })),
          ),
        );
        // A stored unit that no longer exists must not silently 404 every
        // request — fall back to the server's default.
        const known = state.units.some((u) => u.id === state.current);
        if (!known) {
          const fallback = action.payload.default_unit;
          state.current = state.units.some((u) => u.id === fallback)
            ? fallback
            : state.units[0]?.id || null;
          // No unit at all (a fresh installation): select nothing, so no
          // page asks the server for a unit that is not there.
          if (state.current) localStorage.setItem(STORAGE_KEY, state.current);
          else localStorage.removeItem(STORAGE_KEY);
        }
      })
      .addCase(loadOrganization.rejected, (state) => {
        state.status = "error";
      });
  },
});

export const { setUnit } = slice.actions;
export default slice.reducer;

export const selectUnit = (state) => state.unit.current;
// Site-scoped users only see units of their sites; [] = unrestricted.
export const selectUnits = createSelector(
  [(state) => state.unit.units, (state) => state.auth?.user?.sites],
  (units, scope) =>
    scope?.length ? units.filter((u) => scope.includes(u.siteId)) : units,
);
export const selectUnitStatus = (state) => state.unit.status;
// The hierarchy has loaded and holds no production unit the user may work
// in — a fresh installation before its first plant and unit are added.
export const selectNoUnits = (state) =>
  state.unit.status === "ready" && selectUnits(state).length === 0;
export const selectUnitName = (state) =>
  state.unit.units.find((u) => u.id === state.unit.current)?.name ?? state.unit.current;
